/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Component } from 'react';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useLocale } from '@/hooks/useLocale';
import { usePageType } from '@/hooks/usePageType';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { useAppData } from '@/hooks/useAppData';
import { useProductDetails } from '@/features/product/api/getProductDetails';

class ForteTargetingErrorBoundary extends Component<{ children: React.ReactNode }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

/**
 * Hidden div exposing targeting attributes for Forte A/B testing.
 * Renders on SSR for immediate availability, updates reactively on client.
 */
const ForteTargetingInner = () => {
  const { data: headerData } = useHeaderData();
  const { appData } = useAppData();
  const locale = useLocale();
  const { pageType } = usePageType();
  const { data: productDetails } = useProductDetails({ enabled: false });

  const vehicle = getPreferredVehicle(headerData);
  const vehicleMap = headerData?.vehicleMap;
  const store = headerData?.storeDetails;
  const cart = headerData?.miniCartMap;
  const account = headerData?.myAccountMap;

  const isAuthenticated = appData.userAuthenticated;
  const isAnonymous = account?.anonymous !== 'false';
  const hasVehicle = !!vehicle?.catalogVehicleId;
  const hasItemsInCart = (cart?.itemCount ?? 0) > 0;

  return (
    <div
      id="az-targeting"
      data-testid="az-targeting"
      style={{ display: 'none' }}
      aria-hidden="true"
      data-az-authenticated={isAuthenticated}
      data-az-anonymous={isAnonymous}
      data-az-rewards-id={account?.rewardsId ?? ''}
      data-az-user-type={account?.userType ?? ''}
      data-az-customer-id={account?.customerID ?? ''}
      data-az-vehicle-year={vehicle?.year ?? ''}
      data-az-vehicle-make={vehicle?.makeName ?? vehicle?.make ?? ''}
      data-az-vehicle-model={vehicle?.modelName ?? vehicle?.model ?? ''}
      data-az-vehicle-engine={vehicle?.engineName ?? vehicle?.engine ?? ''}
      data-az-vehicle-id={vehicle?.catalogVehicleId ?? ''}
      data-az-vehicle-count={vehicleMap?.vehicleCount ?? '0'}
      data-az-has-vehicle={hasVehicle}
      data-az-store-number={store?.storeNumber ?? headerData?.storeNumber ?? ''}
      data-az-store-zip={store?.zip ?? ''}
      data-az-store-city={store?.city ?? ''}
      data-az-store-state={store?.state ?? ''}
      data-az-bopus-enabled={store?.bopusEnabled ?? false}
      data-az-cart-item-count={cart?.itemCount ?? 0}
      data-az-cart-subtotal={cart?.orderSubTotal ?? 0}
      data-az-has-items-in-cart={hasItemsInCart}
      data-az-locale={locale}
      data-az-page-type={pageType ?? ''}
      data-az-device-type={appData.deviceType}
      data-az-part-group-id={productDetails?.product?.partGroupId ?? ''}
    />
  );
};

export const ForteTargeting = () => (
  <ForteTargetingErrorBoundary>
    <ForteTargetingInner />
  </ForteTargetingErrorBoundary>
);
